import { createContext, useContext, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, MessageSquareText, X, Siren } from "lucide-react";
import { SITE } from "../config/site";
import { EASE } from "./Reveal";

const UrgentContext = createContext(() => {});
export const useUrgent = () => useContext(UrgentContext);

export function UrgentProvider({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <UrgentContext.Provider value={() => setOpen(true)}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            key="urgent-backdrop"
            data-testid="urgent-modal"
            className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-900/60 backdrop-blur-sm sm:items-center sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          >
            <motion.div
              className="w-full max-w-md rounded-t-3xl bg-white p-7 shadow-2xl sm:rounded-3xl"
              initial={{ y: 80, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between">
                <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-800">
                  <Siren className="h-3.5 w-3.5" /> 24/7 Dispatch
                </span>
                <button
                  data-testid="urgent-modal-close"
                  aria-label="Close"
                  className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                  onClick={() => setOpen(false)}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900">
                Need Testing Now?
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Post-accident, reasonable-suspicion, or last-minute testing — call or text and a
                certified collector is dispatched to your location.
              </p>
              <p
                data-testid="urgent-modal-phone"
                className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900"
              >
                {SITE.phoneDisplay}
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <a
                  data-testid="urgent-call-button"
                  href={`tel:${SITE.phoneTel}`}
                  className="flex items-center justify-center gap-2 rounded-full bg-teal-700 px-4 py-3.5 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-teal-800"
                >
                  <Phone className="h-4 w-4" /> Call Now
                </a>
                <a
                  data-testid="urgent-text-button"
                  href={`sms:${SITE.phoneSms}`}
                  className="flex items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-3.5 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-slate-700"
                >
                  <MessageSquareText className="h-4 w-4" /> Text Us
                </a>
              </div>
              <p className="mt-4 text-center text-xs text-slate-500">
                Prefer to plan ahead? Use the Request Testing form and we will confirm a time.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </UrgentContext.Provider>
  );
}

export function UrgentButton({ className = "", testId = "need-testing-now-button" }) {
  const openUrgent = useUrgent();
  return (
    <button
      data-testid={testId}
      onClick={openUrgent}
      className={`animate-pulse-soft inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-amber-600 ${className}`}
    >
      <Siren className="h-4 w-4" />
      Need Testing Now?
    </button>
  );
}
